import { ApiRoutes, AppPaginationOptions, QueryError, WithPagination } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import { Domain, DomainRegluing, SiteDomainsQueryKeys } from '@/modules/Domains';
import { DomainsApiProviders } from '@/modules/DomainsApi';
import { Site } from '@/modules/Sites';

type AllSiteDomainResult = WithPagination<
  Pick<
    Domain,
    'id' | 'name' | 'isDeployed' | 'isHttps' | 'isSitesMain' | 'deployStatus' | 'wwwRedirect' | 'isGoogleVerificated'
  > & {
    regluings: (Pick<DomainRegluing, 'id' | 'forVisitor'> & { targetDomain: Pick<Domain, 'id' | 'name'> })[];
    siteHaveSearchConsoleAccount: boolean;
  }
>;
export const useGetAllSiteDomainsQuery = (id: string, paginationOptions: AppPaginationOptions) =>
  buildQuery<AllSiteDomainResult, QueryError>([SiteDomainsQueryKeys.GetAllDomains, id, paginationOptions], async () => {
    const { data } = await apiExecutor.get(ApiRoutes.SiteDomains.replace(':siteId', id), {
      params: paginationOptions,
    });
    return data;
  });

export const useGetByIdSiteDomainsQuery = (siteId: string, domainId: string) =>
  buildQuery<
    Pick<
      Domain,
      | 'id'
      | 'isDeployed'
      | 'isHttps'
      | 'name'
      | 'staticPrefix'
      | 'isSitesMain'
      | 'wwwRedirect'
      | 'deployStatus'
      | 'googleVerificationToken'
      | 'isGoogleVerificated'
      | 'googleVerificationErrorMessage'
      | 'isIncludedInSearchConsole'
      | 'isAccessDenied'
      | 'lastCachePurgedAt'
    > & {
      dnsRecord: {
        id: string;
        apiProvider: DomainsApiProviders | null;
      };
      site: Pick<Site, 'id' | 'name'> & {
        searchConsoleAccount: Pick<NonNullable<Site['searchConsoleAccount']>, 'id'> | null;
      };
    },
    QueryError
  >([SiteDomainsQueryKeys.GetByIdDomains, siteId, domainId], async () => {
    const { data } = await apiExecutor.get(ApiRoutes.SiteDomainsById.replace(':siteId', siteId).replace(':domainId', domainId));
    return data;
  });

export const useAutocompleteDomainsBySiteIdQuery = (params: { query: string; includedIds?: string[] }, siteId: string) =>
  buildQuery<Pick<Domain, 'id' | 'name'>[], QueryError>(
    [SiteDomainsQueryKeys.AutocompleteDomainsBySiteId, siteId, params],
    async () => {
      const { data } = await apiExecutor.get<Pick<Domain, 'id' | 'name'>[]>(
        ApiRoutes.SiteDomainsAutocomplete.replace(':siteId', siteId),
        { params: { q: params.query, includedIds: params?.includedIds?.length ? String(params.includedIds) : undefined } },
      );
      return data;
    },
  );

export const useAddChangeMainDomainTaskMutation = (siteId: string, domainId: string) =>
  buildMutation<void, QueryError, void>([SiteDomainsQueryKeys.ChangeMainDomain, siteId, domainId], async () => {
    const { data } = await apiExecutor.post<void>(
      ApiRoutes.SiteDomainsByIdChangeMain.replace(':siteId', siteId).replace(':domainId', domainId),
    );
    return data;
  });

export const useAutocompleteAllGoogleVerificatedDomainsBySiteIdQuery = (
  params: { query: string; includedIds?: string[] },
  siteId: string,
) =>
  buildQuery<Pick<Domain, 'id' | 'name' | 'isGoogleVerificated'>[], QueryError>(
    [SiteDomainsQueryKeys.AutocompleteGoogleVerificated, siteId, { ...params, select: 'isGoogleVerificated' }],
    async () => {
      const { data } = await apiExecutor.get<Pick<Domain, 'id' | 'name' | 'isGoogleVerificated'>[]>(
        ApiRoutes.SiteDomainsAutocomplete.replace(':siteId', siteId),
        {
          params: {
            select: 'isGoogleVerificated',
          },
        },
      );
      return data;
    },
  );

export const useDenyAccessSiteDomainMutation = (siteId: string, domainId: string) =>
  buildMutation<void, QueryError, void>([SiteDomainsQueryKeys.DenyAccess, siteId, domainId], async () => {
    const { data } = await apiExecutor.post<void>(
      ApiRoutes.SiteDomainsByIdDenyAccess.replace(':siteId', siteId).replace(':domainId', domainId),
    );
    return data;
  });

export const usePurgeCacheSiteDomainMutation = (siteId: string, domainId: string) =>
  buildMutation<Pick<Domain, 'lastCachePurgedAt'>, QueryError, void>([SiteDomainsQueryKeys.PurgeCache, siteId, domainId], async () => {
    const { data } = await apiExecutor.post(
      ApiRoutes.SiteDomainsByIdPurgeCache.replace(':siteId', siteId).replace(':domainId', domainId),
    );
    return data;
  });
